import React, { createContext, useEffect, useMemo, useState, useRef, useCallback } from "react";
import * as Sentry from '@sentry/react';
import toast from "react-hot-toast";
import { useQueryClient } from '@tanstack/react-query';
import { AuthService } from './authService';
import { Usuario, AuthContextType, AuthProviderProps, LoginCredentials, AuthError } from '../types';
import { AUTH_STORAGE_KEY, AUTH_ERROR_MESSAGES } from '../constants';
import { useFiltersStore } from '@/store/filtersStore';


export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<Usuario | null>(null);
  const [jwt, setJwt] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(AUTH_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<AuthError | null>(null);

  // Track if component is mounted to prevent state updates on unmounted components
  const isMountedRef = useRef<boolean>(true);

  // AbortController for canceling requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup function for canceling ongoing requests
  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    // Component mounted
    isMountedRef.current = true;

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      cancelRequest();
    };
  }, [cancelRequest]);

  const getUser = useCallback(async (): Promise<void> => {
    if (!jwt) return;

    // Cancel previous request if any
    cancelRequest();

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    if (isMountedRef.current) {
      setLoading(true);
    }

    try {
      const response = await AuthService.getUser({ token: jwt });

      if (isMountedRef.current) {
        // The response.data should contain { user: Usuario } based on the service typing
        if (response.data && response.data.user) {
          const resolvedUser = { ...response.data.user, originalPermissions: response.data.user.permissions || [] };
          setUser(resolvedUser);
          Sentry.setUser({ id: String(resolvedUser.id), email: resolvedUser.email, username: resolvedUser.name });
        } else {
          // Try alternative response structures - maybe response.data is the user directly
          const userData = response.data as any;
          if (userData && typeof userData === 'object' && userData.id) {
            const resolvedUser = { ...userData as Usuario, originalPermissions: userData.permissions || [] };
            setUser(resolvedUser);
            Sentry.setUser({ id: String(resolvedUser.id), email: resolvedUser.email, username: resolvedUser.name });
          } else {
            setError({
              message: AUTH_ERROR_MESSAGES.INVALID_RESPONSE,
              errorData: response.data
            });
          }
        }
        setLoading(false);
      }
    } catch (err: unknown) {
      // Only handle error if it's not an abort and component is still mounted
      const error = err as { name?: string; response?: any };
      if (error.name !== 'AbortError' && isMountedRef.current) {
        const isAuthError = error.response?.status === 401 || error.response?.status === 403;

        setError({
          message: isAuthError
            ? AUTH_ERROR_MESSAGES.SESSION_EXPIRED
            : AUTH_ERROR_MESSAGES.USER_DATA_ERROR,
          errorData: err
        });
        setLoading(false);

        // Only clear session on authentication errors (401/403)
        if (isAuthError) {
          setUser(null);
          setJwt(null);
          try {
            window.localStorage.removeItem(AUTH_STORAGE_KEY);
          } catch {
            // Ignore storage errors
          }
        }
      }
    }
  }, [jwt, cancelRequest]);

  useEffect(() => {
    if (jwt) {
      getUser();
    } else if (isMountedRef.current) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt]);

  useEffect(() => {
    if (error) {
      const { message } = error;
      toast.error(message);
    }
  }, [error]);

  const login = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    const { email, password } = credentials;

    // Cancel previous request if any
    cancelRequest();

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    if (isMountedRef.current) {
      setLoading(true);
    }

    try {
      const response = await AuthService.login({ email, password });

      if (isMountedRef.current) {
        // Extract token from response - handle both nested and flat structures
        const token = (response.data as any).data?.token || (response.data as any).token;

        if (token) {
          try {
            window.localStorage.setItem(AUTH_STORAGE_KEY, token);
            setJwt(token);
          } catch {
            // Handle localStorage errors
            setError({
              message: AUTH_ERROR_MESSAGES.STORAGE_ERROR,
              errorData: 'localStorage not available'
            });
          }
        } else {
          setError({
            message: AUTH_ERROR_MESSAGES.TOKEN_MISSING,
            errorData: response.data
          });
        }
        setLoading(false);
      }
    } catch (err: unknown) {
      // Only handle error if it's not an abort and component is still mounted
      const error = err as { name?: string; response?: any };
      if (error.name !== 'AbortError' && isMountedRef.current) {
        setLoading(false);
        setError({
          message: AUTH_ERROR_MESSAGES.LOGIN_ERROR,
          errorData: err
        });
      }
    }
  }, [cancelRequest]);

  const logout = useCallback(async (): Promise<void> => {
    if (!jwt) return;

    // Cancel previous request if any
    cancelRequest();

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    if (isMountedRef.current) {
      setLoading(true);
    }

    try {
      await AuthService.logout({ token: jwt });
    } catch (err) {
      // Log but don't show error to user for logout
      console.warn('Error during logout:', err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setUser(null);
        setJwt(null);
        Sentry.setUser(null);
      }

      // Clear React Query cache (including preferences)
      queryClient.clear();

      // Always clean up localStorage, even if component is unmounted
      try {
        window.localStorage.removeItem(AUTH_STORAGE_KEY);

        // Remove preferences fallback if exists
        if (user?.id) {
          window.localStorage.removeItem(`preferences-${user.id}`);
        }
      } catch {
        // Ignore localStorage errors
      }

      // Clear old filter preferences from sessionStorage (migration cleanup)
      try {
        window.sessionStorage.removeItem('tramites-filters');
      } catch {
        // Ignore sessionStorage errors
      }

      // Clear in-memory filters store to avoid stale state after logout
      useFiltersStore.getState().clear();

      // Clear old column visibility from localStorage (migration cleanup)
      try {
        const keys = Object.keys(window.localStorage);
        keys.forEach((key) => {
          if (key.startsWith('tramites-column-visibility-')) {
            window.localStorage.removeItem(key);
          }
        });
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [jwt, cancelRequest, queryClient, user?.id]);

  const isRole = useCallback((name: string): boolean => {
    const roles = user?.roles || [];
    return roles.includes(name);
  }, [user?.roles]);

  const isSuperAdmin = useCallback((): boolean => {
    return isRole('super_admin');
  }, [isRole]);

  const value: AuthContextType = useMemo(() => ({
    user,
    jwt,
    isAuth: Boolean(jwt && user),
    setUser,
    login,
    loading,
    logout,
    isRole,
    isSuperAdmin,
    error,
    clearError
  }), [jwt, user, loading, error, login, logout, isRole, clearError, setUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
